import React, { useState, useEffect } from 'react';
import Modal from '../Common/Modal';
import '../Common/Common.css';

function CouponFormModal({ isOpen, onClose, onSubmit, coupon = null }) {
  const [formData, setFormData] = useState({
    code: '',
    type: 'percentage',
    value: '',
    description: '',
    min_order_amount: '',
    max_uses: '',
    max_uses_per_user: '',
    start_date: '',
    expiry_date: '',
    is_active: true,
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (coupon) {
      setFormData({
        code: coupon.code || '',
        type: coupon.type || 'percentage',
        value: coupon.value || '',
        description: coupon.description || '',
        min_order_amount: coupon.min_order_amount || '',
        max_uses: coupon.max_uses || '',
        max_uses_per_user: coupon.max_uses_per_user || '',
        start_date: coupon.start_date ? formatDateTimeLocal(coupon.start_date) : '',
        expiry_date: coupon.expiry_date ? formatDateTimeLocal(coupon.expiry_date) : '',
        is_active: coupon.is_active !== undefined ? coupon.is_active : true,
      });
    } else {
      setFormData({
        code: '',
        type: 'percentage',
        value: '',
        description: '',
        min_order_amount: '',
        max_uses: '',
        max_uses_per_user: '',
        start_date: '',
        expiry_date: '',
        is_active: true,
      });
    }
    setErrors({});
  }, [coupon, isOpen]);

  const formatDateTimeLocal = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'code') {
      // Convert to uppercase and allow only alphanumeric, dashes, and underscores
      const sanitizedValue = value.toUpperCase().replace(/[^A-Z0-9_-]/g, '');
      setFormData(prev => ({ ...prev, [name]: sanitizedValue }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Le code est requis';
    } else if (!/^[A-Z0-9_-]+$/.test(formData.code)) {
      newErrors.code = 'Le code ne peut contenir que des lettres majuscules, chiffres, tirets et underscores';
    }

    if (!formData.type) {
      newErrors.type = 'Le type est requis';
    }

    if (!formData.value || formData.value <= 0) {
      newErrors.value = 'La valeur doit être supérieure à 0';
    }

    if (formData.type === 'percentage' && formData.value > 100) {
      newErrors.value = 'Le pourcentage ne peut pas dépasser 100%';
    }

    if (formData.min_order_amount && formData.min_order_amount < 0) {
      newErrors.min_order_amount = 'Le montant minimum doit être positif';
    }

    if (formData.max_uses && formData.max_uses < 1) {
      newErrors.max_uses = 'Le nombre maximum d\'utilisations doit être au moins 1';
    }

    if (formData.max_uses_per_user && formData.max_uses_per_user < 1) {
      newErrors.max_uses_per_user = 'Le nombre maximum d\'utilisations par utilisateur doit être au moins 1';
    }

    if (formData.start_date && formData.expiry_date) {
      if (new Date(formData.start_date) >= new Date(formData.expiry_date)) {
        newErrors.expiry_date = 'La date d\'expiration doit être après la date de début';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setSubmitting(true);
    try {
      // Prepare data for submission
      const submitData = {
        code: formData.code,
        type: formData.type,
        value: parseFloat(formData.value),
        description: formData.description || null,
        min_order_amount: formData.min_order_amount ? parseFloat(formData.min_order_amount) : null,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        max_uses_per_user: formData.max_uses_per_user ? parseInt(formData.max_uses_per_user) : null,
        start_date: formData.start_date || null,
        expiry_date: formData.expiry_date || null,
        is_active: formData.is_active,
      };

      await onSubmit(submitData);
      onClose();
    } catch (error) {
      console.error('Error submitting coupon:', error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={coupon ? 'Modifier le coupon' : 'Ajouter un coupon'} size="large">
      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div>
            <label>Code *</label>
            <input
              type="text"
              name="code"
              value={formData.code}
              onChange={handleChange}
              placeholder="EX: PROMO2025"
              disabled={submitting}
            />
            {errors.code && <div className="error-text">{errors.code}</div>}
          </div>

          <div>
            <label>Type *</label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              disabled={submitting}
            >
              <option value="percentage">Pourcentage</option>
              <option value="fixed">Montant fixe</option>
            </select>
            {errors.type && <div className="error-text">{errors.type}</div>}
          </div>

          <div>
            <label>Valeur * {formData.type === 'percentage' ? '(%)' : '(DH)'}</label>
            <input
              type="number"
              name="value"
              value={formData.value}
              onChange={handleChange}
              placeholder={formData.type === 'percentage' ? '10' : '50'}
              step="0.01"
              min="0"
              disabled={submitting}
            />
            {errors.value && <div className="error-text">{errors.value}</div>}
          </div>
        </div>

        <div className="form-row">
          <div style={{ gridColumn: 'span 3' }}>
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Description du coupon..."
              rows="3"
              disabled={submitting}
            />
            {errors.description && <div className="error-text">{errors.description}</div>}
          </div>
        </div>

        <div className="form-row">
          <div>
            <label>Montant minimum de commande (DH)</label>
            <input
              type="number"
              name="min_order_amount"
              value={formData.min_order_amount}
              onChange={handleChange}
              placeholder="Ex: 500"
              step="0.01"
              min="0"
              disabled={submitting}
            />
            {errors.min_order_amount && <div className="error-text">{errors.min_order_amount}</div>}
          </div>

          <div>
            <label>Utilisations max (total)</label>
            <input
              type="number"
              name="max_uses"
              value={formData.max_uses}
              onChange={handleChange}
              placeholder="Ex: 100"
              min="1"
              disabled={submitting}
            />
            {errors.max_uses && <div className="error-text">{errors.max_uses}</div>}
          </div>

          <div>
            <label>Utilisations max par utilisateur</label>
            <input
              type="number"
              name="max_uses_per_user"
              value={formData.max_uses_per_user}
              onChange={handleChange}
              placeholder="Ex: 1"
              min="1"
              disabled={submitting}
            />
            {errors.max_uses_per_user && <div className="error-text">{errors.max_uses_per_user}</div>}
          </div>
        </div>

        <div className="form-row">
          <div>
            <label>Date de début</label>
            <input
              type="datetime-local"
              name="start_date"
              value={formData.start_date}
              onChange={handleChange}
              disabled={submitting}
            />
            {errors.start_date && <div className="error-text">{errors.start_date}</div>}
          </div>

          <div>
            <label>Date d'expiration</label>
            <input
              type="datetime-local"
              name="expiry_date"
              value={formData.expiry_date}
              onChange={handleChange}
              disabled={submitting}
            />
            {errors.expiry_date && <div className="error-text">{errors.expiry_date}</div>}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', paddingTop: '28px' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: 0 }}>
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                disabled={submitting}
                style={{ width: 'auto', marginRight: '8px' }}
              />
              <span>Actif</span>
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={onClose} className="btn-secondary" disabled={submitting}>
            Annuler
          </button>
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? 'Enregistrement...' : coupon ? 'Mettre à jour' : 'Créer'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default CouponFormModal;
